import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar, Clock } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkEmoji from 'remark-emoji';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import axios from 'axios';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/posts/slug/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching post:', error);
      setError('Post not found');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMMM dd, yyyy');
    } catch {
      return 'Recently';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-zinc-400">Loading post...</div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen pt-16 flex flex-col items-center justify-center" data-testid="post-error">
        <p className="text-zinc-400 text-lg mb-4">{error || 'Post not found'}</p>
        <Link to="/blog">
          <Button variant="outline" className="border-zinc-700">
            <ArrowLeft className="mr-2 w-4 h-4" /> Back to Blog
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16" data-testid="blog-post-page">
      <article className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link to="/blog" data-testid="back-to-blog">
            <Button variant="ghost" className="mb-8 -ml-4 text-zinc-400 hover:text-emerald-400">
              <ArrowLeft className="mr-2 w-4 h-4" /> Back to Blog
            </Button>
          </Link>

          {/* Featured Image */}
          {post.featured_image && (
            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={post.featured_image}
                alt={post.title}
                className="w-full h-96 object-cover"
                data-testid="post-featured-image"
              />
            </div>
          )}

          {/* Post Header */}
          <header className="mb-8">
            <div className="flex flex-wrap gap-2 mb-4">
              {post.tags.map((tag, i) => (
                <Badge
                  key={i}
                  className="bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20"
                  data-testid={`post-tag-${i}`}
                >
                  #{tag}
                </Badge>
              ))}
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold mb-4 leading-tight" data-testid="post-title">
              {post.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-zinc-500">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span data-testid="post-date">{formatDate(post.published_at || post.created_at)}</span>
              </div>
            </div>
          </header>

          {/* Post Excerpt */}
          {post.excerpt && (
            <div className="text-xl text-zinc-400 mb-8 pb-8 border-b border-zinc-800" data-testid="post-excerpt">
              {post.excerpt}
            </div>
          )}

          {/* Post Content */}
          <div className="markdown-preview rounded-xl p-8 shadow-sm" data-testid="post-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkEmoji]}
              components={{
                code({node, inline, className, children, ...props}) {
                  const match = /language-(\w+)/.exec(className || '');
                  return !inline && match ? (
                    <SyntaxHighlighter
                      style={vscDarkPlus}
                      language={match[1]}
                      PreTag="div"
                      {...props}
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  ) : (
                    <code className={className} {...props}>
                      {children}
                    </code>
                  );
                }
              }}
            >
              {post.content}
            </ReactMarkdown>            
          </div>

          {/* Post Footer */}
          <footer className="mt-12 pt-8 border-t border-zinc-800">
            <div className="flex flex-wrap gap-2">
              <span className="text-zinc-500 mr-2">Categories:</span>
              {post.categories.length > 0 ? (
                post.categories.map((category, i) => (
                  <Badge key={i} variant="outline" className="border-zinc-700">
                    {category}
                  </Badge>
                ))
              ) : (
                <span className="text-zinc-500">Uncategorized</span>
              )}
            </div>
          </footer>
        </div>
      </article>
    </div>
  );
};

export default BlogPostPage;
